import React, { useState, useEffect } from "react";
import { getShopMscBalance, createWithdrawalRequest, getShopWithdrawalRequests } from "../utils/api";

function ShopWithdrawal() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState({ balanceMsc: 0, balanceFiat: 0, currency: "USD", fxRate: 1 });
  const [requests, setRequests] = useState([]);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadBalance() {
    try {
      const data = await getShopMscBalance();
      setBalance(data);
    } catch (err) {
      setError(err.message || "Failed to load balance");
    }
  }

  async function loadRequests() {
    try {
      const data = await getShopWithdrawalRequests();
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message || "Failed to load requests");
    }
  }

  useEffect(() => {
    Promise.all([loadBalance(), loadRequests()]).finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    const requestedMsc = parseFloat(amount);
    if (!requestedMsc || requestedMsc <= 0) {
      setError("Invalid amount");
      return;
    }

    if (requestedMsc > balance.balanceMsc) {
      setError(`Insufficient balance. You have ${balance.balanceMsc.toFixed(2)} MSC.`);
      return;
    }

    setSubmitting(true);
    try {
      const data = await createWithdrawalRequest({ requestedMsc, fiatCurrency: balance.currency || "USD" });
      setSuccess(data.message || "Request submitted");
      setAmount("");
      await loadBalance();
      await loadRequests();
    } catch (err) {
      setError(err.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page-responsive">
        <div className="text-lg font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-responsive space-y-6">
      <div>
        <h1 className="text-2xl font-black">MSC Withdrawal</h1>
        <p className="text-sm text-muted mt-1">Convert your MSC earnings to fiat currency.</p>
      </div>

      {/* Balance Card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="text-sm font-bold text-muted mb-2">Available Balance</div>
        <div className="text-3xl font-black">{balance.balanceMsc.toFixed(2)} MSC</div>
        <div className="text-sm text-muted mt-1">
          ≈ {balance.currency} {balance.balanceFiat.toFixed(2)} (1 MSC = {balance.currency} {balance.fxRate.toFixed(4)})
        </div>
      </div>

      {/* Withdrawal Form */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-black mb-4">Request Withdrawal</h2>
        
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-400">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2">Amount (MSC)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={balance.balanceMsc}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Max: ${balance.balanceMsc.toFixed(2)}`}
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2"
              disabled={submitting}
            />
            {amount && (
              <div className="mt-2 text-sm text-muted">
                You will receive: {balance.currency} {(parseFloat(amount) * balance.fxRate).toFixed(2)}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !amount || parseFloat(amount) <= 0}
            className="w-full rounded-lg bg-purple-600 py-3 font-black hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting..." : "Submit Withdrawal Request"}
          </button>
        </form>

        <div className="mt-4 text-xs text-muted">
          • Withdrawals are reviewed by admin within 24 hours<br />
          • Funds are sent to your configured payout method (Stripe or PayPal)<br />
          • Minimum withdrawal: 10 MSC
        </div>
      </div>

      {/* Request History */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-black mb-4">Withdrawal History</h2>
        
        {requests.length === 0 ? (
          <div className="text-sm text-muted">No withdrawal requests yet.</div>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div
                key={req._id}
                className="rounded-lg border border-border bg-secondary p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-black">
                    {req.requestedMsc.toFixed(2)} MSC → {req.requestedFiatCurrency} {req.requestedFiatAmount.toFixed(2)}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {new Date(req.createdAt).toLocaleDateString()} • {req.status}
                  </div>
                  {req.rejectionReason && (
                    <div className="text-xs text-red-400 mt-1">Reason: {req.rejectionReason}</div>
                  )}
                </div>
                <div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      req.status === "completed"
                        ? "bg-green-500/20 text-green-300"
                        : req.status === "rejected"
                        ? "bg-red-500/20 text-red-300"
                        : req.status === "processing"
                        ? "bg-blue-500/20 text-blue-300"
                        : "bg-yellow-500/20 text-yellow-300"
                    }`}
                  >
                    {req.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShopWithdrawal;
